import React, { useState, useEffect } from "react";
import axiosInstance from "apiHandles/axiosInstance";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import "../assets/css/SidebarComponents/Description.css";
import { useDarkMode } from "context/DarkModeProvider";
import { flashcardsTranslation } from "./english"
import "../assets/css/colors.css";
import { useLanguage } from "context/LanguageProvider";
import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY


const colorSchemes = {
  HashTable: { primary: '#10b981', secondary: '#059669', accent: '#d1fae5' },
  Tree: { primary: '#3b82f6', secondary: '#2563eb', accent: '#dbeafe' },
  List: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#ede9fe' },
  Stack: { primary: '#f59e0b', secondary: '#d97706', accent: '#fef3c7' },
  Queue: { primary: '#ef4444', secondary: '#dc2626', accent: '#fecaca' },
  Graph: { primary: '#06b6d4', secondary: '#0891b2', accent: '#cffafe' },
  Array: { primary: '#ec4899', secondary: '#db2777', accent: '#fce7f3' },
  Set: { primary: '#84cc16', secondary: '#65a30d', accent: '#ecfccb' },
  //Python: { primary: '#3776ab', secondary: '#d3d3d3' }
  Python: { primary: '#4B8BBE', secondary: '#306998', accent: '#e0e0e0' },
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  const cleanPath = imagePath.trim();
  if (cleanPath.startsWith("http")) {
    return cleanPath;
  }

  if (cleanPath.startsWith("/media/")) {
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const finalUrl = `${baseUrl}${cleanPath}`;
    return finalUrl;
  }

  let publicUrl;

  if (cleanPath.startsWith("/")) {
    publicUrl = `${process.env.PUBLIC_URL}${cleanPath}`;
  } else {
    publicUrl = `${process.env.PUBLIC_URL}/${cleanPath}`;
  }

  return publicUrl;
}

function parseBoldText(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={index}>{boldText}</strong>;
    }
    return part;
  });
}

function parseCodeBlocks(text) {
  if (!text) return { hasCodeBlocks: false, content: text };

  const codeBlockRegex = /CODEBLOCK\s*\n([\s\S]*?)\n\s*CODEBLOCK/g;

  if (!codeBlockRegex.test(text)) {
    return { hasCodeBlocks: false, content: text };
  }

  codeBlockRegex.lastIndex = 0;

  const result = [];
  let lastIndex = 0;
  let match;
  let blockIndex = 0;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      if (beforeText.trim()) {
        result.push({ type: 'text', content: beforeText });
      }
    }

    result.push({
      type: 'codeblock',
      content: match[1].trim(),
      key: `codeblock-${blockIndex++}`
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    const afterText = text.slice(lastIndex);
    if (afterText.trim()) {
      result.push({ type: 'text', content: afterText });
    }
  }

  return { hasCodeBlocks: true, content: result };
}

function detectLanguage(code) {
  if (!code) return '';

  const trimmedCode = code.trim();

  if (trimmedCode.includes('def ') ||
    trimmedCode.includes('import ') ||
    trimmedCode.includes('from ') ||
    trimmedCode.includes('if __name__') ||
    trimmedCode.includes('print(') ||
    trimmedCode.includes('for ') && trimmedCode.includes('in ') ||
    trimmedCode.includes('range(')) {
    return 'Python';
  }

  if (trimmedCode.includes('function ') ||
    trimmedCode.includes('const ') ||
    trimmedCode.includes('let ') ||
    trimmedCode.includes('var ') ||
    trimmedCode.includes('=>') ||
    trimmedCode.includes('console.log')) {
    return 'JavaScript';
  }

  if (trimmedCode.includes('public class') ||
    trimmedCode.includes('private ') ||
    trimmedCode.includes('System.out.println') ||
    trimmedCode.includes('public static void main')) {
    return 'Java';
  }

  if (trimmedCode.includes('#include') ||
    trimmedCode.includes('int main()') ||
    trimmedCode.includes('printf(') ||
    trimmedCode.includes('cout <<')) {
    return 'C++';
  }

  if (trimmedCode.includes('SELECT ') ||
    trimmedCode.includes('FROM ') ||
    trimmedCode.includes('WHERE ') ||
    trimmedCode.includes('INSERT INTO')) {
    return 'SQL';
  }

  if (trimmedCode.includes('<html>') ||
    trimmedCode.includes('<div>') ||
    trimmedCode.includes('<body>')) {
    return 'HTML';
  }

  if (trimmedCode.includes('{') && trimmedCode.includes('}') &&
    (trimmedCode.includes(':') || trimmedCode.includes('color:') || trimmedCode.includes('font-'))) {
    return 'CSS';
  }

  return 'Code';
}

function renderCodeBlockContent(parsedResult, lineProcessor) {
  if (!parsedResult.hasCodeBlocks) {
    return lineProcessor(parsedResult.content);
  }

  const elements = [];

  parsedResult.content.forEach((item, index) => {
    if (item.type === 'codeblock') {
      const detectedLanguage = detectLanguage(item.content);
      elements.push(
        <CodeBlock key={item.key} code={item.content} language={detectedLanguage} />
      );
    } else if (item.type === 'text') {
      const textElements = lineProcessor(item.content);
      elements.push(...textElements);
    }
  });

  return elements;
}

function decryptData(encryptedData) {
  try {
    // Δημιουργία 32-byte key από το SECRET_KEY (ίδιο με backend)
    const key = CryptoJS.SHA256(SECRET_KEY);

    const decrypted = CryptoJS.AES.decrypt(
      encryptedData,
      key,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
}

const CodeBlock = ({ code, language, selLanguage }) => {
  const [copied, setCopied] = useState(false);
  const isDarkMode = localStorage.getItem("darkMode") === "true";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // fallback για παλιά browsers
      const textarea = document.createElement("textarea");
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ position: 'relative', margin: '16px 0' }}>
      {/* Top bar με γλώσσα + κουμπί αντιγραφής */}
      <div style={{
        position: 'absolute',
        top: '-12px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        zIndex: 1,
      }}>
        {/* Language badge */}
        <div style={{
          backgroundColor: isDarkMode ? '#334155' : '#e2e8f0',
          color: isDarkMode ? '#94a3b8' : '#334155',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          border: isDarkMode ? '1px solid #475569' : '1px solid #cbd5e1',
        }}>
          {language}
        </div>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          title="Αντιγραφή κώδικα"
          style={{
            backgroundColor: copied ? '#10b981' : (isDarkMode ? '#334155' : '#e2e8f0'),
            color: copied ? '#ffffff' : (isDarkMode ? '#94a3b8' : '#334155'),
            border: `1px solid ${copied ? '#10b981' : (isDarkMode ? '#475569' : '#cbd5e1')}`,
            borderRadius: '12px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {copied ? (
            <>{selLanguage ? "✓ Αντιγράφηκε" : "✓ Copied"}</>
          ) : (
            <>{selLanguage ? "⧉ Αντιγραφή" : "⧉ Copy"}</>
          )}
        </button>
      </div>

      {/* Code block */}
      <pre style={{
        backgroundColor: isDarkMode ? '#1e293b' : '#f4f1f180',
        color: isDarkMode ? '#e2e8f0' : '#0f172a',
        padding: '16px',
        borderRadius: '8px',
        border: isDarkMode ? '1px solid #334155' : '1px solid #0f0f1058',
        overflow: 'auto',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        fontSize: '14px',
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
        margin: 0,
      }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const Index = () => {
  const [stageData, setStageData] = useState(null);
  const [allSubStructures, setallSubStructures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentScheme, setCurrentScheme] = useState(() => {
    const s = localStorage.getItem("structureLocation");
    return colorSchemes[s] || colorSchemes.Graph;
  });
  const navigate = useNavigate();

  const { darkMode } = useDarkMode();
  const { selLanguage, toggleLanguage, IsLoading } = useLanguage();

  const fetchStageById = async (stageId) => {
    try {
      const response = await axiosInstance.get(`/api/stage/${stageId}/description/`);
      const decryptedData = decryptData(response.data);
      return decryptedData;
    }
    catch (error) {
      if (error.response?.status === 401) {
        alert("Πρέπει να κάνεις login ξανά");
        localStorage.removeItem("access");
        navigate("/login");
      } else {
        alert("Σφάλμα κατά τη φόρτωση του stage: " + error.message);
      }
      return null;
    }
  };

  function parseTextWithImages(text) {

    if (!text) return [];

    const codeBlockResult = parseCodeBlocks(text);

    if (codeBlockResult.hasCodeBlocks) {
      return renderCodeBlockContent(codeBlockResult, (textContent) => {
        return parseTextLines(textContent);
      });
    }

    return parseTextLines(text);
  }

  function parseTextLines(text) {
    const s = localStorage.getItem("structureLocation");
    const scheme = colorSchemes[s] || colorSchemes.Graph;

    const lines = text.split("\n");
    const elements = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith("{image:")) {
        const src = trimmed.replace("{image:", "").replace("}", "").trim();

        const imageUrl = getImageUrl(src);


        elements.push(
          <div key={`img-${index}`} style={{ textAlign: "center", margin: "20px 0", padding: "10px", }}>
            <div className="debug-info" style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "10px" }}>
              🔍 Trying to load: {imageUrl}
            </div>

            <img
              src={imageUrl}
              alt="Example Image"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                border: "2px solid #334155",
              }}
              onError={(e) => {
                console.error('❌ Failed to load image:', imageUrl);
                console.error('❌ Full URL attempted:', e.target.src);
                e.target.style.display = 'none';
                const errorMsg = e.target.parentNode.querySelector('.error-message');
                if (errorMsg) errorMsg.style.display = 'block';
              }}
              onLoad={(e) => {

                const debugInfo = e.target.parentNode.querySelector('.debug-info');
                if (debugInfo) debugInfo.style.display = 'none';
              }}
            />
            <div className="error-message">
              ⚠️ Η εικόνα δεν μπόρεσε να φορτωθεί: <code>{src}</code>
              <br /><small>Βεβαιωθείτε ότι το αρχείο υπάρχει στον σωστό φάκελο</small>
              <br /><small>Tried URL: {imageUrl}</small>
            </div>
          </div>
        );
        return;
      }

      if (trimmed === "" || trimmed.length === 0) {
        elements.push(
          <div key={`empty-${index}`} style={{ height: "1em" }} />
        );
        return;
      }

      if (trimmed === "/n" || trimmed === "---") {
        elements.push(
          <div key={`divider-${index}`} style={{ height: "2em", borderBottom: "1px solid #475569", margin: "1em 0" }} />
        );
        return;
      }

      if (trimmed.includes("====")) {
        elements.push(
          <div key={`indent4-${index}`} style={{ marginLeft: "4em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed.replace(/====/g, ""))}
          </div>
        );
        return;
      }

      if (trimmed.startsWith(">> >>")) {
        elements.push(
          <div key={`indent-double-${index}`} style={{ marginLeft: "4em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000", fontStyle: "normal" }}>
            {parseBoldText(trimmed.replace(/^>> >>\s?/, ""))}
          </div>
        );
        return;
      }

      if (trimmed.startsWith("###")) {
        elements.push(
          <div key={`header-${index}`} style={{
            fontWeight: "800", background: `none`,
            backdropFilter: 'blur(10px)',
            transition: "background 0.3s",
            fontWeight: "700",
            fontSize: "1rem",
            color: "rgb(122, 142, 255)",
            cursor: "pointer",
            fontSize: "1.1em", margin: "1em 0 0.5em 0"
          }}>
            {parseBoldText(trimmed.replace(/^###\s?/, ""))}
          </div>
        );
        return;
      }

      if (trimmed.startsWith(">>")) {
        elements.push(
          <div key={`indent-${index}`} style={{ marginLeft: "2em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed.replace(/^>>\s?/, ""))}
          </div>
        );
        return;
      }

      if (trimmed) {
        elements.push(
          <div key={`text-${index}`} style={{ margin: "0.5em 0", lineHeight: "1.6", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed)}
          </div>
        );
      }
    });

    return elements;
  }

  const fetchSubStructures = async () => {
    try {
      const response = await axiosInstance.get("api/substructure/");
      setallSubStructures(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        alert("Πρέπει να κάνεις login ξανά");
        localStorage.removeItem("access");
        navigate("/login");
      } else {
        alert("Σφάλμα: " + error.message);
      }
    }
  };

  useEffect(() => {
    const loadStageData = async () => {
      setIsLoading(true);

      try {
        // ✅ Προσπάθησε να φορτώσεις από το ID
        const selectedStageId = localStorage.getItem("selectedStageId");

        if (selectedStageId) {

          const stage = await fetchStageById(selectedStageId);

          if (stage) {
            setStageData(stage);
            await fetchSubStructures();
          } else {
            console.warn("⚠️ Failed to load stage from ID");
          }
        } else {
          console.warn("⚠️ No selectedStageId found in localStorage");
        }

        // Ενημέρωση του χρωματικού σχήματος
        const s = localStorage.getItem("structureLocation");
        setCurrentScheme(colorSchemes[s] || colorSchemes.Graph);
      } catch (error) {
        console.error("❌ Error in loadStageData:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStageData();
  }, [navigate]);

  useEffect(() => {
    if (stageData) {
      console.log("✅ Stage data loaded:", stageData);
      console.log("📝 Examples:", stageData.examples);
    }
  }, [stageData]);

  if (isLoading) {
    return (
      <Container>
        <div className="description-container">
          <Card className="content-card">
            <CardBody>
              <h3 style={{ color: darkMode ? "#fff" : "#000" }}>Loading...</h3>
              <p style={{ color: darkMode ? "#cbd5e1" : "#64748b" }}>Please wait...</p>
            </CardBody>
          </Card>
        </div>
      </Container>
    );
  }

  if (!stageData) {
    return (
      <Container>
        <div className="description-container">
          <Card className="content-card">
            <CardBody>
              <h3 style={{ color: darkMode ? "#fff" : "#000" }}>No stage selected</h3>
              <p style={{ color: darkMode ? "#cbd5e1" : "#64748b" }}>
                Please select a stage from the diagram to view its details.
              </p>
            </CardBody>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container className={`background-container ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <Col className="description-container" xs="12" sm="12" md="12" lg="12" xl="12">
        <Row className="mb-4 justify-content-center">
          <Col xl="12" lg="12" md="12" sm="12" xs="12">
            <Col className="firstCard-column">
              <Card className="descriptionIndex-card"
                style={{
                  borderRadius: "24px",
                  // border: darkMode ? `1px solid ${currentScheme.secondary}30` : `1px solid ${currentScheme.secondary}90`,
                  background: darkMode ? "#1e293b" : "#f8fafc",
                }}>
                <CardHeader
                  style={{ borderRadius: "24px", }}>
                  <p style={{
                    backdropFilter: 'blur(10px)',
                    fontWeight: "700",
                    fontSize: "24px",
                    transition: "background 0.3s",
                    fontWeight: "700",
                    background: "rgb(122, 142, 255)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",

                  }}>
                    {selLanguage ? "Περιγραφή" : "Description"}</p>
                </CardHeader>
                <CardBody style={{ borderRadius: "24px", background: darkMode ? "#1e293b" : "#f8fafc" }}>
                  <DescriptionTabs description={selLanguage ? stageData.description : stageData.description_en} scheme={currentScheme} darkMode={darkMode} />
                </CardBody>
              </Card>
            </Col>
          </Col>
        </Row>

        <Row className="mb-4 justify-content-center">
          <Col xl="12" lg="12" md="12" sm="12" xs="12">
            <Col className="secondCard-column">
              <Card className="examplesIndex-card"
                style={{
                  borderRadius: "24px",
                  // border: darkMode ? `1px solid ${currentScheme.secondary}30` : `1px solid ${currentScheme.secondary}90`,
                  // boxShadow: `0 10px 25px -8px ${currentScheme.secondary}30`,
                  background: darkMode ? "#1e293b" : "#f8fafc",

                }}>
                <CardHeader style={{
                  borderRadius: "24px",
                  background: darkMode ? "#1e293b" : "#f8fafc",
                  height: "60px",

                }}>
                  <p style={{
                    background: `none`,
                    background: darkMode ? "#1e293b" : "#f8fafc",
                    backdropFilter: 'blur(10px)',
                    fontWeight: "700",
                    fontSize: "24px",
                    transition: "background 0.3s",
                    color: "rgb(122, 142, 255)",
                  }}>
                    {selLanguage ? "Παράδειγμα" : "Example"}
                  </p>
                </CardHeader>

                <CardBody>
                  {stageData.examples && stageData.examples.length > 0 ? (
                    stageData.examples.map((example, index) => (
                      <Col
                        key={index} className="markdown-content"
                        style={{ marginBottom: "2rem" }}
                      >
                        {example.text && example.text.includes('***') ? (
                          <ExampleTabs exampleText={selLanguage ? example.text : example.english_text} />
                        ) : (
                          parseTextWithImages(selLanguage ? example.text : example.english_text)
                        )}
                      </Col>
                    ))
                  ) : (
                    <p>No examples available.</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Col>
        </Row>
      </Col >
    </Container >
  );
};

export default Index;

function parseDescriptionIntoTabs(description) {
  if (!description) return [];

  const parts = description.split("***");
  const tabs = [];

  for (let i = 1; i < parts.length; i++) {
    const section = parts[i].trim();
    const lines = section.split("\n");
    const title = lines[0]?.trim() || "Untitled";
    const sectionContent = lines.slice(1).join('\n');
    const codeBlockResult = parseCodeBlocks(sectionContent);

    if (codeBlockResult.hasCodeBlocks) {
      const renderedContent = renderCodeBlockContent(codeBlockResult, (textContent) => {
        return processDescriptionLines(textContent);
      });
      tabs.push({ title, content: renderedContent });
    } else {
      tabs.push({ title, content: processDescriptionLines(sectionContent) });
    }
  }

  function processDescriptionLines(text) {
    const s = localStorage.getItem("structureLocation");
    const scheme = colorSchemes[s] || colorSchemes.Graph;

    const lines = text.split("\n");
    const processedLines = [];

    for (let j = 0; j < lines.length; j++) {
      const trimmed = lines[j].trim();

      if (trimmed === "" || trimmed.length === 0) {
        processedLines.push(<div key={`empty-${j}`} style={{ height: "1em" }} />);
        continue;
      }

      if (trimmed === "/n" || trimmed === "---") {
        processedLines.push(<div key={`divider-${j}`} style={{ height: "2em", borderBottom: "1px solid #475569", margin: "1em 0" }} />);
        continue;
      }

      if (trimmed.startsWith("{image:")) {
        const src = trimmed.replace("{image:", "").replace("}", "").trim();
        const imageUrl = getImageUrl(src);

        processedLines.push(
          <div key={`image-${j}`} style={{ textAlign: "center", margin: "20px 0", padding: "10px", borderRadius: "8px" }}>
            <img
              src={imageUrl}
              alt="Description Image"
              style={{ maxWidth: "100%", height: "auto", borderRadius: "12px", boxShadow: "inset 0 4px 12px rgba(0,0,0,0.2)" }}
              onError={(e) => {
                console.error('Failed to load image:', imageUrl);
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
              onLoad={() => console.log('Image loaded successfully:', imageUrl)}
            />
            <div style={{ display: 'none', color: '#ef4444', fontSize: '0.9rem', marginTop: '12px', padding: '8px', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fecaca' }}>
              ⚠️ Η εικόνα δεν μπόρεσε να φορτωθεί: <code>{src}</code>
              <br /><small>Βεβαιωθείτε ότι το αρχείο υπάρχει στον σωστό φάκελο</small>
            </div>
          </div>
        );
        continue;
      }

      if (trimmed.startsWith(">> >>")) {
        processedLines.push(
          <div key={`indent-double-${j}`} style={{ marginLeft: "4em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000", fontStyle: "normal" }}>
            {parseBoldText(trimmed.replace(/^>> >>\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed.startsWith(">>")) {
        processedLines.push(
          <div key={`indent-${j}`} style={{ marginLeft: "2em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed.replace(/^>>\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed.startsWith("###")) {
        processedLines.push(
          <div key={`header-${j}`}
            style={{
              fontWeight: "800", background: `none`,
              backdropFilter: 'blur(10px)',
              transition: "background 0.3s",
              fontWeight: "700",
              fontSize: "1rem",
              color: "rgb(122, 142, 255)",
              cursor: "pointer",
              fontSize: "1.1em", margin: "1em 0 0.5em 0"
            }}>
            {parseBoldText(trimmed.replace(/^###\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed) {
        processedLines.push(
          <div key={`text-${j}`} style={{ margin: "0.5em 0", lineHeight: "1.6", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed)}
          </div>
        );
      }
    }

    return processedLines;
  }

  return tabs;
}

function parseExampleIntoTabs(exampleText) {
  if (!exampleText) return [];

  const parts = exampleText.split("***");
  const tabs = [];

  for (let i = 1; i < parts.length; i++) {
    const section = parts[i].trim();
    const lines = section.split("\n");
    const title = lines[0]?.trim() || "Untitled";
    const sectionContent = lines.slice(1).join('\n');
    const codeBlockResult = parseCodeBlocks(sectionContent);

    if (codeBlockResult.hasCodeBlocks) {
      const renderedContent = renderCodeBlockContent(codeBlockResult, (textContent) => {
        return processExampleLines(textContent);
      });
      tabs.push({ title, content: renderedContent });
    } else {
      tabs.push({ title, content: processExampleLines(sectionContent) });
    }
  }

  function processExampleLines(text) {
    const lines = text.split("\n");
    const processedLines = [];

    for (let j = 0; j < lines.length; j++) {
      const trimmed = lines[j].trim();

      if (trimmed === "" || trimmed.length === 0) {
        processedLines.push(<div key={`empty-${j}`} style={{ height: "1em" }} />);
        continue;
      }

      if (trimmed === "/n" || trimmed === "---") {
        processedLines.push(<div key={`divider-${j}`} style={{ height: "2em", borderBottom: "1px solid #475569", margin: "1em 0", }} />);
        continue;
      }

      if (trimmed.startsWith("{image:")) {
        const src = trimmed.replace("{image:", "").replace("}", "").trim();
        const imageUrl = getImageUrl(src);

        processedLines.push(
          <div key={`image-${j}`} style={{ textAlign: "center", margin: "20px 0", padding: "10px", borderRadius: "8px" }}>
            <img
              src={imageUrl}
              alt="Example Image"
              style={{ maxWidth: "100%", height: "auto", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.2)", border: "2px solid #334155" }}
              onError={(e) => {
                console.error('Failed to load image:', imageUrl);
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
              onLoad={() => console.log('Image loaded successfully:', imageUrl)}
            />
            <div style={{ display: 'none', color: '#ef4444', fontSize: '0.9rem', marginTop: '12px', padding: '8px', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fecaca' }}>
              ⚠️ Η εικόνα δεν μπόρεσε να φορτωθεί: <code>{src}</code>
              <br /><small>Βεβαιωθείτε ότι το αρχείο υπάρχει στον σωστό φάκελο</small>
            </div>
          </div>
        );
        continue;
      }

      if (trimmed.includes("====")) {
        processedLines.push(
          <div key={`indent4-${j}`} style={{ marginLeft: "4em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed.replace(/====/g, ""))}
          </div>
        );
        continue;
      }

      if (trimmed.startsWith(">> >>")) {
        processedLines.push(
          <div key={`indent-double-${j}`} style={{ marginLeft: "4em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000", fontStyle: "normal" }}>
            {parseBoldText(trimmed.replace(/^>> >>\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed.startsWith(">>")) {
        processedLines.push(
          <div key={`indent-${j}`} style={{ marginLeft: "2em", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed.replace(/^>>\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed.startsWith("###")) {
        processedLines.push(
          <div key={`header-${j}`} style={{ fontWeight: "800", color: "#7A8EFF", fontSize: "1.1em", margin: "1em 0 0.5em 0" }}>
            {parseBoldText(trimmed.replace(/^###\s?/, ""))}
          </div>
        );
        continue;
      }

      if (trimmed) {
        processedLines.push(
          <div key={`text-${j}`} style={{ margin: "0.5em 0", lineHeight: "1.6", color: localStorage.getItem('darkMode') === 'true' ? "#ffffff" : "#000000" }}>
            {parseBoldText(trimmed)}
          </div>
        );
      }
    }

    return processedLines;
  }

  return tabs;
}

const ExampleTabs = ({ exampleText }) => {
  const tabs = parseExampleIntoTabs(exampleText);
  const [activeTab, setActiveTab] = useState(0);

  if (tabs.length === 0) return <p>Κανένα example διαθέσιμο.</p>;



  return (
    <>
      <Nav tabs className="mb-3">
        {tabs.map((tab, index) => (
          <NavItem key={index}>
            <NavLink
              className={classnames("tab-link", { active: activeTab === index })}
              onClick={() => setActiveTab(index)}
              style={{
                color: "#7a8effff",
                borderBottom: activeTab === index ? "1px solid #5e72e4" : "none",
                borderLeft: activeTab === index ? "1px solid #5e72e4" : "none",
                borderRight: activeTab === index ? "1px solid #5e72e4" : "none",
                borderTop: activeTab === index ? "1px solid #5e72e4" : "none",
                fontWeight: activeTab === index ? "normal" : "normal",
                backgroundColor: activeTab === index ? "transparent" : "transparent",
                boxShadow: activeTab === index ? "0 1px 2px #5e72e4" : "none",
                cursor: "pointer"
              }}
            >
              {tab.title}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId={activeTab}>
          <div className="markdown-content">
            {tabs[activeTab]?.content.map((block, idx) => (
              <div key={idx}>{block}</div>
            ))}
          </div>
        </TabPane>
      </TabContent>
    </>
  );
};

const DescriptionTabs = ({ description, scheme, darkMode }) => {
  const tabs = parseDescriptionIntoTabs(description);
  const [activeTab, setActiveTab] = useState(0);

  if (tabs.length === 0) return <p>Καμία περιγραφή διαθέσιμη.</p>;

  return (
    <>
      <Nav tabs className="mb-3">
        {tabs.map((tab, index) => (
          <NavItem key={index}>
            <NavLink
              className={classnames("tab-link", { active: activeTab === index })}
              onClick={() => setActiveTab(index)}
              style={{
                background: 'transparent',
                color: "rgb(122, 142, 255)",
                WebkitBackgroundClip: "text",
                border: activeTab === index ? `2px solid ${scheme.primary}90` : `1px solid rgba(202, 202, 202, 1)`,
                fontWeight: "700",
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              {tab.title}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId={activeTab} >
          <div className="markdown-content">
            {tabs[activeTab]?.content.map((block, idx) => (
              <div key={idx}>{block}</div>
            ))}
          </div>
        </TabPane>
      </TabContent>
    </>
  );
};